import * as React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import logo from './res/beringwaters-group-v.png';
import { Backdrop, Button, CircularProgress, IconButton } from '@mui/material';
import { CSSProperties, useEffect, useState } from 'react';
import Field from './Field';
import CloseIcon from '@mui/icons-material/Close';

const albumWindowDimentions = {width: 1440, height: 900};
const portretWindowDimentions = {width: 450};
const contactEmail = 'contact@beringwaters.com';
const emailJsServiceId = 'service_BW_contact';
const emailJsTemplateId = 'template_BW_contact';
const emailJsPublicKey = 'D-pkuEb-fGI0SpU9S';

export default function ContactFormApp() {
  const { innerWidth: width, innerHeight: height } = window;
  const [windowDimensions, setWindowDimensions] = useState({width, height});
  useEffect(() => {
    function handleResize() {
      const { innerWidth: width, innerHeight: height } = window;
      setWindowDimensions({width, height});
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [firstName, setFirstName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastName, setLastName] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageError, setMessageError] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(false);
  const [isReset, setReset] = useState<boolean>(false);

  useEffect(() => { 
    setFirstName('');
    setLastName('');
    setEmail('');
    setMessage('');
    setFirstNameError(false);
    setLastNameError(false);
    setEmailError(false);
    setMessageError(false);
    setSubmitError('');
    setReset(false) 
  }, [isReset]);
  useEffect(() => { !isReset && setFirstNameError(false) }, [firstName]);
  useEffect(() => { !isReset && setLastNameError(false) }, [lastName]);
  useEffect(() => { !isReset && setEmailError(false) }, [email]);
  useEffect(() => { !isReset && setMessageError(false) }, [message]);


  const isAlbum = windowDimensions.height < windowDimensions.width;
  const relation = windowDimensions.height / windowDimensions.width;
  const relationMuliplicator = relation >= 0.75 ? 1.2 : relation >= 0.625 ? 1.1 : 1;

  const zoomByWidth = (albumDimention: number, portretDimention: number) => isAlbum 
    ? albumDimention / albumWindowDimentions.width * windowDimensions.width * relationMuliplicator
    : portretDimention / portretWindowDimentions.width * windowDimensions.width;

  const zoomByHeight = (albumDimention: number, portretDimention: number) => isAlbum 
    ? albumDimention / albumWindowDimentions.height * windowDimensions.height
    : zoomByWidth(albumDimention, portretDimention);

  const titleFontSize = zoomByWidth(42, 42);
  const titleFontHeight = zoomByWidth(50, 50);
  const bodyFontSize = zoomByWidth(24, 14);
  const bodyFontHeight = zoomByWidth(29, 20);
  const fieldFontSize = zoomByWidth(14, 14);
  const fieldFontHeight = zoomByWidth(17, 20);

  const logoHeight = zoomByHeight(43*2, 32);
  const logoWidth = zoomByHeight(147*2, 110);

  const mtTitle = zoomByWidth(0, 100);
  const mbTitle = zoomByWidth(0, 25);

  const boxPaddingTop = zoomByHeight(60, 20);
  const boxPaddingOther = zoomByHeight(120, 20);
  const boxMargin = zoomByWidth(0, 10);

  const fieldsGap = zoomByWidth(10, 10);
  const mbField = zoomByHeight(7, 7);
  const buttonWidth = zoomByWidth(233, 233);
  const buttonHeight = zoomByHeight(44, 44);
  
  const typoStyle = {
    fontFamily: "Inter",
    letterSpacing: "0%",
    color: "#154482",
  };
  const titleStyle = {
    ...typoStyle,
    fontWeight: 800,
    fontSize: `${titleFontSize}px`,
    lineHeight: `${titleFontHeight}px`,
    marginTop: `${isAlbum ? 0 : mtTitle}px`
  };
  const bodyBaseStyle = {
    ...typoStyle,
    fontWeight: 400,
    fontSize: `${bodyFontSize}px`,
    lineHeight: `${bodyFontHeight}px`,
  }
  const bodyStyle = {
    ...bodyBaseStyle,
    marginTop: `${fieldsGap}px`
  }
  const fieldStyle: CSSProperties = {
    ...typoStyle,
    fontWeight: 500,
    fontSize: `${fieldFontSize}px`,
    lineHeight: `${fieldFontHeight}px`,
    marginBottom: `${mbField}px`,
  }
  const buttonStyle: CSSProperties = {
    fontFamily: "Inter",
    letterSpacing: "0%",
    fontWeight: 500,
    fontSize: `${fieldFontSize}px`,
    lineHeight: `${fieldFontHeight}px`,
    marginBottom: `${mbField}px`,
    height: `${buttonHeight}px`,
    textWrap: 'nowrap',
    ...(isAlbum ? {width: `${buttonWidth}px`} : {}),
  }

  const boxStyle: CSSProperties = {
    padding: `${boxPaddingTop}px ${boxPaddingOther}px ${boxPaddingOther}px ${boxPaddingOther}px`,
    display: "flex", 
    flexDirection: 'column',
    ...(
      isAlbum ? {
        width: "100vw", 
        height: "100vh", 
      } : {
        margin: `${boxMargin}px`,
      }
    )
  };

  const validateEmail = () => {
    return email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  async function sendData() {
    setSubmitError('');

    if (!firstName) setFirstNameError(true);
    if (!lastName) setLastNameError(true);
    if (!email || !validateEmail()) setEmailError(true);
    if (!message) setMessageError(true);

    if(!firstName || !lastName || !email || !validateEmail() || !message) return;

    setLoading(true);
    const fullName = `${firstName} ${lastName}`.trim();

    const formData = new FormData();
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    formData.append('email', email);
    formData.append('message', message);

    try {
      await fetch('https://script.google.com/macros/s/AKfycbwrx9rXzwKHXgYXat6QyXrhN2Fx869vPEO_KxRs5dohnFW3LRumn5a6xpmDjiVKhUlk/exec', {
        method: 'POST',
        body: formData,
      });
    } catch (error) {
      console.warn(error);
    }

    try {
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_id: emailJsServiceId,
          template_id: emailJsTemplateId,
          user_id: emailJsPublicKey,
          template_params: {
            firstName,
            lastName,
            name: fullName,
            user_name: fullName,
            from_name: fullName,
            from_email: contactEmail,
            reply_to: email,
            visitor_email: email,
            user_email: email,
            to_email: contactEmail,
            title: 'Contact form submission',
            time: new Date().toISOString(),
            email,
            message
          }
        }),
      });

      if (!response.ok) {
        const details = await response.text();
        throw new Error(details || `EmailJS request failed with status ${response.status}`);
      }

      setLoading(false);
      reset();
    } catch (error) {
      console.error(error);
      setLoading(false);
      setSubmitError(`We could not send your message. Please email ${contactEmail} directly.`);
    }
  }

  const firstNameField = <Field 
    name='First Name*' autocomplete='given-name' type='text' inputName='firstName'
    value={firstName} onChange={(e) => setFirstName(e.target.value)} 
    error={firstNameError} errorMessage={'Please enter your first name'}
    fieldStyle={fieldStyle} bodyBaseStyle={bodyBaseStyle} />

  const lastNameField = <Field 
    name='Last Name*' autocomplete='family-name' type='text' inputName='lastName'
    value={lastName} onChange={(e) => setLastName(e.target.value)} 
    error={lastNameError} errorMessage={'Please enter your last name'}
    fieldStyle={fieldStyle} bodyBaseStyle={bodyBaseStyle} />

  const reset = () => {
    setReset(true);
    window.location.href = '../'
  }

  return (
    <Container sx={{m: 0}} style={{ padding: 0 }}>
      <Box style={boxStyle}> 
        <Box style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            justifyContent: 'space-between',
            }}>
          <Box
            component="img"
            sx={{
              height: logoHeight,
              width: logoWidth,
              maxHeight: logoHeight,
              maxWidth: logoWidth,
            }}
            src={logo}
          />
          <IconButton 
            style={{ marginTop: '-8px', marginRight: '-8px' }}
            onClick={reset}
            >
            <CloseIcon />
          </IconButton>    
        </Box>
        {isAlbum && <Box sx={{ flex: 1 }}/>}
        <Typography style={titleStyle} >
          Contact us
        </Typography>
        <Typography style={bodyStyle}>
          We’re Listening - Reach Out to Our Team Today.
        </Typography>
        {isAlbum && <Box sx={{ flex: 1 }}/>}
        <Box style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: `${fieldsGap}px`,
          ...(!isAlbum ? {marginTop: `${mbTitle}px`} : {})
          }}>
          {isAlbum && <Box style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: `${fieldsGap}px`, 
            }}>
            {firstNameField}
            {lastNameField}            
          </Box>}
          {!isAlbum && firstNameField}
          {!isAlbum && lastNameField}
          <Field 
            name='Email address*' autocomplete='off' type='email' inputName='email'
            value={email} onChange={(e) => setEmail(e.target.value)}
            error={emailError} errorMessage={'Please enter your email'}
            fieldStyle={fieldStyle} bodyBaseStyle={bodyBaseStyle} />
          <Field 
            name='Message*' autocomplete='off' type='text' multiline={true}
            value={message} onChange={(e) => setMessage(e.target.value)} 
            error={messageError}  errorMessage={'Please enter your message'}
            fieldStyle={fieldStyle} bodyBaseStyle={bodyBaseStyle} />
          <Button 
            variant="contained" 
            color='primary' 
            style={buttonStyle}
            onClick={sendData}>
            Let’s connect
          </Button>
          {submitError && <Typography style={{ ...fieldStyle, color: '#b00020' }}>
            {submitError}
          </Typography>}
          <Backdrop
            sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>        
      </Box>
    </Container>
  );
}
